import type { IAddon, IData, IDataPoint, IEvent } from 'Components/sq-graphics';
import { isNumber, isPartialDate, partialDateFromDate, partialDateToValue, sortPartialDate } from 'neuro-utils';
import {
  bai,
  bdi,
  des,
  eq5d,
  fsmc,
  fss,
  gds15,
  mgQol15r,
  msis,
  msnq,
  pdq8,
  pedsql,
  treatmentMonitoringInquiry,
  Doc_Progress,
  ess,
} from 'neuro-schemas';
import type { TFSSOption } from 'neuro-schemas/fss';
import { filterDocumentsByType, omitControlProps } from 'Utility/documentHandling';
import { getNinmtTreatmentPeriodsDateValues } from 'Components/DashboardGraph/Utils';
import { difference, equals } from 'ramda';
import colors from '../../../../config/theme/colors';
import { parkinsonSurveyTypes, srSurveyTypes } from 'Routes/MyService/Document/config';

const { calculateScore: calculateBDIScore } = bdi;
const { calculateScore: calculateBAIScore } = bai;
const { calculateScore: calculateESSScore } = ess;
const { calculateScore: calculatePDQ8Score } = pdq8;

const getPlatformMyDocs = (platform: Platform): TMyDocumentType[] => {
  switch (platform) {
    case 'ms': {
      return ['eq5d', 'fss', 'fsmc', 'msis', 'msnq', 'status', 'predss', 'relapseSuspicion', 'qualityoflife'];
    }
    case 'sleepApnea': {
      return srSurveyTypes;
    }
    case 'parkinson': {
      return parkinsonSurveyTypes;
    }
    default: {
      return [];
    }
  }
};

const getTimelineEventTitleLocalizationKey = (platform: Platform, docType: string, isIrom?: boolean) => {
  switch (platform) {
    case 'parkinson':
    case 'sleepApnea': {
      if (platform === 'parkinson' && docType === 'memory' && isIrom) {
        return 'myService.parkinson.memoryInformant';
      }
      return `myService.${platform}.opts.${docType}`;
    }
    default: {
      return `graph.selfReport.${docType}.title`;
    }
  }
};

export const convertPatientSelfReportEventsToTimeline = (
  docs: IDocumentV2[],
  fm: (id: string) => string,
  dateFromPartialUpdateTimeFrame: (date: PartialDate, time?: Time, marker?: true) => Date,
  platform: Platform,
  uiLang: Locale,
): Array<IAddon> | undefined => {
  if (!Array.isArray(docs) || docs.length === 0) return undefined;
  const platformDocs = getPlatformMyDocs(platform);
  docs = filterDocumentsByType(docs, platformDocs as TDocumentType[]);
  const events: IEvent[] = [];
  docs.forEach(doc => {
    switch (doc._type) {
      case 'relapseSuspicion': {
        if (!isPartialDate(doc.relapseDate)) return;
        events.push({
          date: dateFromPartialUpdateTimeFrame(doc.relapseDate),
          eventType: 'selfReport',
          title: fm(`graph.selfReport.${doc._type}.title`),
          priority: 'normal',
        });
        break;
      }
      case 'gds15': {
        const date = doc.date && isPartialDate(doc.date) ? doc.date : new Date(doc._cdate);
        const pd = isPartialDate(date) ? date : partialDateFromDate(date);
        if (!isPartialDate(pd)) return;
        const gds15Localizations = gds15.localizations;
        const mapScoreToInterpretation = (score: number): string => {
          if (score >= 12 && score <= 15) return 'severeDepression';
          if (score >= 9 && score <= 11) return 'moderateDepression';
          if (score >= 5 && score <= 8) return 'mildDepression';
          return 'noDepression';
        };
        const score =
          typeof Doc_Progress.calculateProgress('gds15', omitControlProps(doc)).yielded === 'number'
            ? (Doc_Progress.calculateProgress('gds15', omitControlProps(doc)).yielded as number)
            : '-';
        const ceiling = Doc_Progress.calculateProgress('gds15', omitControlProps(doc)).ceiling;
        const interpretation =
          typeof score === 'number'
            ? gds15Localizations[uiLang][`interpretation.${mapScoreToInterpretation(score)}`]
            : '-';
        events.push({
          date: dateFromPartialUpdateTimeFrame(pd),
          eventType: 'selfReport',
          title: fm(getTimelineEventTitleLocalizationKey(platform, doc._type)),
          description: [
            { title: gds15Localizations[uiLang]['score'], values: `${score} / ${ceiling}` },
            { title: gds15Localizations[uiLang]['interpretationCategory'], values: interpretation },
          ],
          priority: 'low',
        });
        break;
      }
      default: {
        const date = 'date' in doc && doc.date && isPartialDate(doc.date) ? doc.date : new Date(doc._cdate);
        const pd = isPartialDate(date) ? date : partialDateFromDate(date);
        if (!isPartialDate(pd)) return;
        events.push({
          date: dateFromPartialUpdateTimeFrame(pd),
          eventType: 'selfReport',
          title: fm(getTimelineEventTitleLocalizationKey(platform, doc._type, doc._source === 'irom')),
          priority: 'low',
        });
        break;
      }
    }
  });
  if (events.length === 0) return undefined;
  return [{ id: 'selfReport', title: '', titleDescription: undefined, events }];
};

/**
 * Convert my app msis document into valid data for graph
 *
 * - Makes sure if docs are valid type
 * - Calculate msis totals
 * - Map docs into data points
 *
 * @param {Array<IMSIS & IControlProps>} docs My app documents
 * @param {(date?: PartialDate, marker?: boolean)} dateFromPartialUpdateTimeFrame function declared at DashboardGraph index.tsx
 * @returns {Array<IData> | undefined} Data for graph or undefined
 */
export const convertMsisToGraph = (
  docs: IDocumentWithTypeV2<'msis'>[],
  dateFromPartialUpdateTimeFrame: (date: PartialDate, time?: Time, marker?: true) => Date,
): Array<IData> | undefined => {
  if (docs.length === 0) return undefined;
  const dataPoints: IDataPoint[] = [];
  docs.forEach(d => {
    const date = new Date(d._cdate);
    const values = msis.calculators.calculateMSISTotal(d);
    const value = (values.scores?.mental ?? 0) + (values.scores?.physical ?? 0);
    if (!(value || value === 0)) return;
    dataPoints.push({ id: 'fsmc', date: dateFromPartialUpdateTimeFrame(partialDateFromDate(date)), value });
  });
  return [{ id: 'msis', type: 'lineGraph', legend: 'MSIS-29', dataPoints }];
};

/**
 * Convert my app fsmc document into valid data for graph
 *
 * - Makes sure if docs are valid type
 * - Calculate fsmc totals
 * - Map docs into data points
 *
 * @param {Array<IFSMC & IControlProps>} docs My app documents
 * @param {(date?: PartialDate, marker?: boolean)} dateFromPartialUpdateTimeFrame function declared at DashboardGraph index.tsx
 * @returns {Array<IData> | undefined} Data for graph or undefined
 */
export const convertFsmcToGraph = (
  docs: IDocumentWithTypeV2<'fsmc'>[],
  dateFromPartialUpdateTimeFrame: (date: PartialDate, time?: Time, marker?: true) => Date,
  fm: (id: string) => string,
): Array<IData> | undefined => {
  if (docs.length === 0) return;
  const dataPoints: IDataPoint[] = [];
  docs.forEach(d => {
    const date = d.date ?? new Date(d._cdate);
    const values = fsmc.calculators.calculateFSMCTotal(d);
    const value = (values.scores?.mental ?? 0) + (values.scores?.physical ?? 0);
    if (!(value || value === 0)) return;
    dataPoints.push({
      id: 'fsmc',
      date: dateFromPartialUpdateTimeFrame(isPartialDate(date) ? date : partialDateFromDate(date)),
      value,
      description: [
        {
          title: fm('graph.selfReport.fsmc.physicalFatigue'),
          values: values.scores?.physical || values.scores?.physical === 0 ? values.scores.physical : '-',
        },
        {
          title: fm('graph.selfReport.fsmc.cognitiveFatigue'),
          values: values.scores?.mental || values.scores?.mental === 0 ? values.scores.mental : '-',
        },
      ],
    });
  });
  return [{ id: 'fsmc', type: 'lineGraph', legend: 'FSMC', dataPoints }];
};

/**
 * Convert my app msnq document into valid data for graph
 *
 * - Makes sure if docs are valid type
 * - Calculate msnq totals
 * - Map docs into data points
 *
 * @param {Array<IMSNQ & IControlProps>} docs MSNQ Documents
 * @param {(date?: PartialDate, marker?: boolean)} dateFromPartialUpdateTimeFrame function declared at DashboardGraph index.tsx
 * @returns {Array<IData> | undefined} Data for graph or undefined
 */
export const convertMsnqToGraph = (
  docs: IDocumentWithTypeV2<'msnq'>[],
  dateFromPartialUpdateTimeFrame: (date: PartialDate, time?: Time, marker?: true) => Date,
): Array<IData> | undefined => {
  if (docs.length === 0) return;
  const dataPoints: IDataPoint[] = [];
  docs.forEach(d => {
    const date = d.date ?? new Date(d._cdate);
    const value = msnq.calculators.calculateMSNQTotal(d).score;
    if (!(value || value === 0)) return;
    dataPoints.push({
      id: 'msnq',
      date: dateFromPartialUpdateTimeFrame(isPartialDate(date) ? date : partialDateFromDate(date)),
      value,
    });
  });
  return [{ id: 'msnq', type: 'lineGraph', legend: 'MSNQ', dataPoints }];
};

/**
 * Convert my app fatigue document into valid data for graph
 *
 * - Makes sure if docs are valid type
 * - Calculate fatigue totals
 * - Map docs into data points
 *
 * @param {Array<IControlProps>} docs My app documents
 * @param {(date?: PartialDate, marker?: boolean)} dateFromPartialUpdateTimeFrame function declared at DashboardGraph index.tsx
 * @returns {Array<IData> | undefined} Data for graph or undefined
 */
export const convertFssToGraph = (
  docs: IDocumentWithTypeV2<'fss'>[],
  dateFromPartialUpdateTimeFrame: (date: PartialDate, time?: Time, marker?: true) => Date,
  fm: (id: string) => string,
): Array<IData> | undefined => {
  if (docs.length === 0) return undefined;
  const dataPoints: IDataPoint[] = [];
  docs.forEach(d => {
    const data: IFSS = {};
    Object.entries(d).forEach(([k, d]: [k: string, d: TFSSOption]) => {
      if (
        ![
          'motivation',
          'exercise',
          'tendency',
          'physicalActivity',
          'problems',
          'sustainedPhysicalActivity',
          'duties',
          'disability',
          'life',
        ].includes(k)
      ) {
        return;
      }
      data[k as keyof Omit<IFSS, 'date'>] = d;
    });
    const date = d.date ?? new Date(d._cdate);
    const value = fss.calculateScore(data);
    if (!(value || value === 0)) return;
    dataPoints.push({
      id: 'fss',
      date: dateFromPartialUpdateTimeFrame(isPartialDate(date) ? date : partialDateFromDate(date)),
      value,
    });
  });
  return [{ id: 'fss', type: 'lineGraph', legend: fm('myService.mgravis.opts.fss'), dataPoints }];
};

const isStatus = (s: unknown): s is IStatus => !!s && typeof s === 'object' && Object.keys(s).includes('sections');

/**
 * Convert my app predss document into valid data for graph
 *
 * - Makes sure if docs are valid type
 * - Map docs into predss data points
 *
 * @param {Array<{ sections?: { prEDSS: number } } & IControlProps>} docs My app documents
 * @param {(date?: PartialDate, marker?: boolean)} dateFromPartialUpdateTimeFrame function declared at DashboardGraph index.tsx
 * @returns {Array<IData> | undefined} Data for graph or undefined
 */
export const convertPredssToGraph = (
  docs: Array<IDocumentWithTypeV2<'status' | 'predss'>>,
  dateFromPartialUpdateTimeFrame: (date: PartialDate, time?: Time, marker?: true) => Date,
): Array<IData> | undefined => {
  if (docs.length === 0) return;

  const dataPoints: IDataPoint[] = [];
  docs.forEach(d => {
    const date = d.date ?? new Date(d._cdate);
    if (!isStatus(d)) return;
    const value = d.sections?.prEDSS;
    if (!(value || value === 0)) return;
    dataPoints.push({
      id: 'predss',
      date: dateFromPartialUpdateTimeFrame(isPartialDate(date) ? date : partialDateFromDate(date)),
      value,
    });
  });
  return [{ id: 'predss', type: 'lineGraph', legend: 'prEDSS', dataPoints }];
};

/**
 * Convert my app EQ5D document into valid data for graph
 *
 * - Makes sure if docs are valid type
 * - Map docs into eq5d data points
 *
 * @param {Array<{ IEQ5D } & IControlProps>} docs My app documents
 * @param {(date?: PartialDate, marker?: boolean)} dateFromPartialUpdateTimeFrame function declared at DashboardGraph index.tsx
 * @returns {Array<IData> | undefined} Data for graph or undefined
 */
export const convertEq5dToGraph = (
  docs: IDocumentWithTypeV2<'eq5d'>[],
  dateFromPartialUpdateTimeFrame: (date: PartialDate, time?: Time, marker?: true) => Date,
  level?: '5L' | '3L',
): Array<IData> | undefined => {
  if (docs.length === 0) return;
  const dataPoints: IDataPoint[] = [];
  docs.forEach(d => {
    const date = d.date ?? partialDateFromDate(new Date(d._cdate));
    const value = eq5d.calculateScore(d);
    if (!(value || value === 0)) return;
    dataPoints.push({
      id: 'eq5d',
      date: dateFromPartialUpdateTimeFrame(date),
      value: value < 0 ? '< 0' : value,
      title: value < 0 ? `${value}` : undefined,
      description: [
        {
          title: 'VAS',
          values: d.sections?.yourHealthToday || d.sections?.yourHealthToday === 0 ? d.sections.yourHealthToday : '-',
        },
      ],
    });
  });
  return [{ id: 'eq5d', type: 'lineGraph', legend: `EQ-5D${level ? '-' + level : ''}`, dataPoints }];
};

export const statusSectionKeys: Array<keyof NonNullable<IStatus['sections']>> = [
  'narcomsMobilityVASValue',
  'narcomsHandFunctionVASValue',
  'narcomsVisionVASValue',
  'narcomsFatigueVASValue',
  'narcomsCognitionVASValue',
  'narcomsBladderBowelVASValue',
  'narcomsSexualityVASValue',
  'narcomsSensoryVASValue',
  'narcomsSpasticityVASValue',
  'narcomsPainVASValue',
  'narcomsDepressionVASValue',
  'narcomsTremorVASValue',
  'speechDysfagiaVASValue',
];

/**
 * Convert my app status document into valid data for graph
 *
 * - Makes sure if docs are valid type
 *
 * @param {Array<IStatus & IControlProps>} docs Status documents
 * @param {(date?: PartialDate, marker?: boolean)} dateFromPartialUpdateTimeFrame function declared at DashboardGraph index.tsx
 * @returns {Array<IData> | undefined} Data for graph or undefined
 */
export const convertStatusToGraph = (
  docs: IDocumentWithTypeV2<'status'>[],
  fm: (id: string) => string,
  dateFromPartialUpdateTimeFrame: (date: PartialDate, time?: Time, marker?: true) => Date,
): Array<IData> | undefined => {
  if (docs.length === 0) return;

  const dataMap = Object.fromEntries(statusSectionKeys.flatMap(type => [[type, []]])) as Record<string, IDataPoint[]>;

  const sortedDocs = docs.toSorted((d1, d2) => {
    const firstPartial = d1.date ?? partialDateFromDate(new Date(d1._cdate));
    const secondPartial = d2.date ?? partialDateFromDate(new Date(d2._cdate));
    return sortPartialDate(secondPartial, firstPartial);
  });
  const filteredDocs = sortedDocs.filter((d, i) => {
    const prevDoc = sortedDocs[i - 1];

    if (!prevDoc) return true;

    const date = new Date(d._cdate);
    const prevDate = new Date(prevDoc._cdate);

    return !equals(d.date ?? partialDateFromDate(date), prevDoc.date ?? partialDateFromDate(prevDate));
  });

  let highestSymptom: keyof NonNullable<IStatus['sections']>;
  let biggestChangeToWorse: keyof NonNullable<IStatus['sections']>;

  filteredDocs.forEach((d, i, arr) => {
    const data = d.sections;
    const date = d.date && isPartialDate(d.date) ? d.date : new Date(d._cdate);
    const prevData = i + 1 < arr.length ? arr[i + 1]?.sections : undefined;

    if (!data) return;

    if (i === 0) {
      const highestSymptomKeyValue = Object.entries(data).reduce((prev, curr) => {
        const prevVal = prev[1] ?? 0;
        const currVal = curr[1] ?? 0;
        return currVal > prevVal ? curr : prev;
      });
      highestSymptom = highestSymptomKeyValue[0] as keyof NonNullable<IStatus['sections']>;
    }

    if (i === 0 && prevData) {
      const biggestDiffKeyValue = Object.entries(data).reduce((prev, curr) => {
        const currField = curr[0];
        const currVal = curr[1] ?? 0;
        const difference = currVal - prevData[currField as keyof IStatus['sections']];

        const prevField = prev[0] as keyof NonNullable<IStatus['sections']>;
        const prevVal = prev[1] ?? 0;
        const prevFieldDifference = prevVal - (prevData[prevField] ?? 0);

        return difference > prevFieldDifference ? curr : prev;
      });

      biggestChangeToWorse = biggestDiffKeyValue[0] as keyof NonNullable<IStatus['sections']>;
    }

    statusSectionKeys.forEach(k => {
      const value = data[k];
      isNumber(value) &&
        dataMap[k]?.push({
          id: k,
          date: isPartialDate(date)
            ? dateFromPartialUpdateTimeFrame(date)
            : dateFromPartialUpdateTimeFrame(partialDateFromDate(date)),
          value,
          title: `${value}`,
        });
    });
  });

  const latestSectionValues = filteredDocs[0]?.sections;

  const sortedSectionKeys = statusSectionKeys.toSorted((k1, k2) => {
    const value1 = latestSectionValues?.[k1];
    const value2 = latestSectionValues?.[k2];
    if (isNumber(value1) && isNumber(value2)) {
      return value2 - value1;
    }
    return 0;
  });

  return sortedSectionKeys.map(k => {
    const asterisks = biggestChangeToWorse === k ? '*' : '';
    return {
      dataPoints: dataMap[k],
      id: k,
      legend: fm(`graph.selfReport.status.opts.${k.split('VASValue')[0]}`) + asterisks,
      legendDescription: asterisks
        ? {
            title: fm(`graph.selfReport.status.opts.${k.split('VASValue')[0]}`),
            description: asterisks + fm(`graph.selfReport.status.legendDesc.${asterisks}`),
          }
        : undefined,
      type: 'lineGraph',
      hideByDefault: !(k === highestSymptom || k === biggestChangeToWorse),
      ...(dataMap[k].length > 1 && { linegraphProps: { hidePoints: true } }),
    };
  });
};

export const convertBdiAndBaiToGraph = (
  docs: Array<IDocumentWithTypeV2<'bdi' | 'bai'>>,
  dateFromPartialUpdateTimeFrame: (date: PartialDate, time?: Time, marker?: true) => Date,
  fm: (id: string) => string,
  latestNinmtTreatmentDecisionDate: Date | null,
  types: Array<'bdi' | 'bai'>,
): Array<IData> | undefined => {
  if (docs.length === 0) return undefined;
  if (!latestNinmtTreatmentDecisionDate) return undefined;
  const bdiDataPoints: IDataPoint[] = [];
  const baiDataPoints: IDataPoint[] = [];
  docs.forEach(d => {
    if (!isPartialDate(d.date)) return;
    switch (d._type) {
      case 'bdi': {
        const score = calculateBDIScore(d);
        if (isNumber(score)) {
          bdiDataPoints.push({
            id: 'bdi',
            date: dateFromPartialUpdateTimeFrame(d.date),
            value: score,
            title: String(score),
            description: fm(`myService.ninmt.bdiTotalScoreDescOpts.${bdi.utils.bdiDepressionDesc(score)}`),
          });
        }
        break;
      }
      case 'bai': {
        const score = calculateBAIScore(d);
        if (isNumber(score)) {
          baiDataPoints.push({
            id: 'bai',
            date: dateFromPartialUpdateTimeFrame(d.date),
            value: score,
            title: String(score),
            description: fm(`myService.ninmt.baiAnxietyDescs.${bai.utils.baiAnxietyDesc(score)}`),
          });
        }
        break;
      }
    }
  });
  const dataArr: IData[] = [];
  if (types.includes('bdi')) {
    dataArr.push({ id: 'bdi', type: 'lineGraph', legend: 'BDI', dataPoints: bdiDataPoints });
  }
  if (types.includes('bai')) {
    dataArr.push({ id: 'bai', type: 'lineGraph', legend: 'BAI', dataPoints: baiDataPoints });
  }
  return dataArr;
};

export const convertGicToGraph = (
  docs: IDocumentWithTypeV2<'treatmentMonitoringInquiry'>[],
  dateFromPartialUpdateTimeFrame: (date: PartialDate, time?: Time, marker?: true) => Date,
  treatmentPeriodDocs: IDocumentWithTypeV2<'ninmtTreatmentPeriod'>[],
  uiLang: Locale,
  fm: (id: string) => string,
): Array<IData> | undefined => {
  if (docs.length === 0) return;
  if (treatmentPeriodDocs.length === 0) return;
  const locales = treatmentMonitoringInquiry.localizations[uiLang];
  const sortedTreatmentPeriods = treatmentPeriodDocs.sort((d1, d2) => sortPartialDate(d2.date, d1.date));
  const xLineBreaks = sortedTreatmentPeriods
    .map(tp => (tp.date ? dateFromPartialUpdateTimeFrame(tp.date) : undefined))
    .filter(v => v) as Array<Date>;

  const dataPoints: IDataPoint[] = [];
  docs.forEach(d => {
    if (!isPartialDate(d.date)) return;
    if (!(d.treatmentEffectivenessEstimate || d.treatmentEffectivenessEstimate === 0)) return;
    dataPoints.push({
      id: 'gic',
      date: dateFromPartialUpdateTimeFrame(d.date),
      value: d.treatmentEffectivenessEstimate,
      title: locales[`opts.${d.treatmentEffectivenessEstimate}`],
      description: d.gic ?? undefined,
      pointType: d.gic ? 'rect' : 'circle',
    });
  });
  return [
    {
      id: 'gic',
      type: 'lineGraph',
      legend: 'GIC',
      dataPoints,
      linegraphProps: { xAxisLineBreaks: xLineBreaks },
      legendDescription: { title: 'GIC', description: fm('graph.selfReport.gic.legendDesc') },
    },
  ];
};

export const convertSleepToGraph = (
  treatmentMonitoringInquiries: IDocumentWithTypeV2<'treatmentMonitoringInquiry'>[],
  ninmtPreInquiries: IDocumentWithTypeV2<'ninmtPreInquiry'>[],
  dateFromPartialUpdateTimeFrame: (date: PartialDate, time?: Time, marker?: true) => Date,
  treatmentPeriodDocs: IDocumentWithTypeV2<'ninmtTreatmentPeriod'>[],
  fm: (id: string) => string,
): Array<IData> | undefined => {
  if (treatmentMonitoringInquiries.length === 0 && ninmtPreInquiries.length === 0) return undefined;
  if (treatmentPeriodDocs.length === 0) return;
  const sortedTreatmentPeriods = treatmentPeriodDocs.sort((d1, d2) => sortPartialDate(d2.date, d1.date));
  const xLineBreaks = sortedTreatmentPeriods
    .map(tp => (tp.date ? dateFromPartialUpdateTimeFrame(tp.date) : undefined))
    .filter(v => v) as Array<Date>;
  const qualityPoints: IDataPoint[] = [];
  const quantityPoints: IDataPoint[] = [];

  treatmentMonitoringInquiries.forEach(d => {
    if (!isPartialDate(d.date)) return;
    if (d.sleep?.sleepQuality || d.sleep?.sleepQuality === 0) {
      qualityPoints.push({
        id: 'sleepQuality',
        date: dateFromPartialUpdateTimeFrame(d.date),
        value: d.sleep.sleepQuality,
        description: fm('graph.treatmentMonitoring'),
      });
    }
    if (d.sleep?.sleepQuantity || d.sleep?.sleepQuantity === 0) {
      quantityPoints.push({
        id: 'sleepQuantity',
        date: dateFromPartialUpdateTimeFrame(d.date),
        value: d.sleep.sleepQuantity,
        description: fm('graph.treatmentMonitoring'),
      });
    }
  });

  ninmtPreInquiries.forEach(d => {
    if (!isPartialDate(d.date)) return;
    if (d.sleep?.sleepQuality || d.sleep?.sleepQuality === 0) {
      qualityPoints.push({
        id: 'sleepQuality',
        date: dateFromPartialUpdateTimeFrame(d.date),
        value: d.sleep.sleepQuality,
        pointType: 'rect',
        description: fm('graph.preinquiry'),
      });
    }
    if (d.sleep?.sleepQuantity || d.sleep?.sleepQuantity === 0) {
      quantityPoints.push({
        id: 'sleepQuantity',
        date: dateFromPartialUpdateTimeFrame(d.date),
        value: d.sleep.sleepQuantity,
        description: fm('graph.preinquiry'),
      });
    }
  });

  return [
    {
      id: 'sleepQuantity',
      type: 'stackedBarChart',
      legend: fm('graph.sleepQuantity'),
      dataPoints: quantityPoints,
      stackedBarChartProps: { interval: 'daily' },
      extraLegends: [fm('graph.extraLegends.sleep.quantity')],
    },
    {
      id: 'sleepQuality',
      type: 'lineGraph',
      legend: fm('graph.sleepQuality'),
      dataPoints: qualityPoints,
      extraLegends: [fm('graph.extraLegends.sleep.quality'), fm('graph.extraLegends.sleep.quality2')],
      linegraphProps: { xAxisLineBreaks: xLineBreaks },
    },
  ];
};

export const convertEpisodicSymptomsToGraph = (
  treatmentMonitoringInquiries: IDocumentWithTypeV2<'treatmentMonitoringInquiry'>[],
  ninmtPreInquiries: IDocumentWithTypeV2<'ninmtPreInquiry'>[],
  dateFromPartialUpdateTimeframe: (date: PartialDate, time?: Time, marker?: true) => Date,
  fm: (id: string) => string,
  treatmentPeriodDocs: IDocumentWithTypeV2<'ninmtTreatmentPeriod'>[],
  uiLang: Locale,
): Array<{ data: IData[]; id: string }> | undefined => {
  if (treatmentMonitoringInquiries.length === 0 && ninmtPreInquiries.length === 0) return undefined;
  const sortedTreatmentPeriods = treatmentPeriodDocs.sort((d1, d2) => sortPartialDate(d2.date, d1.date));
  const xLineBreaks = sortedTreatmentPeriods
    .map(tp => (tp.date ? dateFromPartialUpdateTimeframe(tp.date) : undefined))
    .filter(v => v) as Array<Date>;
  const locales = treatmentMonitoringInquiry.localizations[uiLang];

  const allSymptomsData: Array<{ data: IData[]; id: string }> = [];
  const painData: IData[] = [];
  const painAmountDataPoints: IDataPoint[] = [];
  const painDurationDataPoints: IDataPoint[] = [];
  const otherData: Array<IData[]> = [];
  const otherAmountDataPoints: Array<IDataPoint[]> = [];
  const otherDurationDataPoints: Array<IDataPoint[]> = [];

  treatmentMonitoringInquiries.forEach(d => {
    if (!isPartialDate(d.date)) return;
    if (!d.symptom) return;
    if (d.symptom.pain?.episodes) {
      if (d.symptom.pain.episodes.episodes === 'no') {
        // If episodes === 'no' draw point to 0 on graph
        painDurationDataPoints.push({
          id: 'painDuration',
          date: dateFromPartialUpdateTimeframe(d.date),
          value: '0',
          title: '0',
        });
      }
      d.symptom.pain.episodes.episodesInAWeek &&
        painAmountDataPoints.push({
          id: 'painAmount',
          date: dateFromPartialUpdateTimeframe(d.date),
          value: d.symptom.pain.episodes.episodesInAWeek,
          title: locales[`opts.${d.symptom.pain.episodes.episodesInAWeek}`],
          description: fm('graph.treatmentMonitoring'),
        });
      d.symptom.pain.episodes.episodesDuration &&
        painDurationDataPoints.push({
          id: 'painDuration',
          date: dateFromPartialUpdateTimeframe(d.date),
          value: d.symptom.pain.episodes.episodesDuration,
          title: locales[`opts.${d.symptom.pain.episodes.episodesDuration}`],
          description: fm('graph.treatmentMonitoring'),
        });
    }
    if (d.symptom.other) {
      d.symptom.other.forEach(o => {
        if (!o.episodes) return;
        if (!o.symptomDescription) return;
        let allSymptomDataIndex: number;
        let isNew = false;
        if (allSymptomsData.findIndex(d => d.id.includes(o.symptomDescription ?? '')) >= 0) {
          allSymptomDataIndex = allSymptomsData.findIndex(d => d.id.includes(o.symptomDescription ?? ''));
        } else {
          isNew = true;
          allSymptomDataIndex = allSymptomsData.length;
        }
        if (isNew) {
          otherAmountDataPoints[allSymptomDataIndex] = [];
          otherDurationDataPoints[allSymptomDataIndex] = [];
          otherData[allSymptomDataIndex] = [];
        }
        if (o.episodes.episodes === 'no' && d.date) {
          otherDurationDataPoints[allSymptomDataIndex]?.push({
            id: `otherDuration${o.symptomDescription}`,
            date: dateFromPartialUpdateTimeframe(d.date),
            value: '0',
            title: '0',
          });
        }
        o.episodes.episodesInAWeek &&
          d.date &&
          otherAmountDataPoints[allSymptomDataIndex]?.push({
            id: `otherAmount${o.symptomDescription}`,
            date: dateFromPartialUpdateTimeframe(d.date),
            value: o.episodes.episodesInAWeek,
            title: locales[`opts.${o.episodes.episodesInAWeek}`],
          });
        o.episodes.episodesDuration &&
          d.date &&
          otherDurationDataPoints[allSymptomDataIndex]?.push({
            id: `otherDuration${o.symptomDescription}`,
            date: dateFromPartialUpdateTimeframe(d.date),
            value: o.episodes.episodesDuration,
            title: locales[`opts.${o.episodes.episodesDuration}`],
          });
        if (allSymptomsData[allSymptomDataIndex]?.id) {
          return;
        } else {
          allSymptomsData.push({ data: [], id: `${[locales['other.title']]}: ${o.symptomDescription}` });
        }
      });
    }
  });

  ninmtPreInquiries.forEach(d => {
    if (!isPartialDate(d.date)) return;
    if (!d.primarySymptom) return;
    if (d.primarySymptom.isSymptomEpisodic === 'no') {
      painDurationDataPoints.push({
        id: 'painDuration',
        date: dateFromPartialUpdateTimeframe(d.date),
        value: '0',
        title: '0',
        pointType: 'rect',
      });
    }
    d.primarySymptom.averageAmountOfSymptomEpisodes &&
      painAmountDataPoints.push({
        id: 'painAmount',
        date: dateFromPartialUpdateTimeframe(d.date),
        value: d.primarySymptom.averageAmountOfSymptomEpisodes,
        title: locales[`opts.${d.primarySymptom.averageAmountOfSymptomEpisodes}`],
        pointType: 'rect',
        description: fm('graph.preinquiry'),
      });
    d.primarySymptom.averageDurationOfSymptomEpisodes &&
      painDurationDataPoints.push({
        id: 'painDuration',
        date: dateFromPartialUpdateTimeframe(d.date),
        value: d.primarySymptom.averageDurationOfSymptomEpisodes,
        title: locales[`opts.${d.primarySymptom.averageDurationOfSymptomEpisodes}`],
        pointType: 'rect',
        description: fm('graph.preinquiry'),
      });
  });

  otherAmountDataPoints.forEach((oddp, i) => {
    otherData[i] = [
      ...otherData[i],
      {
        id: oddp.find(dp => dp.id)?.id ?? '',
        dataPoints: oddp,
        type: 'stackedBarChart',
        legend: locales['other.episodesQuantity'],
        stackedBarChartProps: { interval: 'daily' },
      },
    ];
  });
  otherDurationDataPoints.forEach((oddp, i) => {
    otherData[i] = [
      ...otherData[i],
      {
        id: oddp.find(dp => dp.id)?.id ?? '',
        dataPoints: oddp,
        type: 'lineGraph',
        useAdditionalScale: true,
        legend: locales['other.episodesDuration'],
      },
    ];
  });
  otherData.forEach((od, i) => {
    allSymptomsData[i].data = od;
  });
  painData.push({
    id: 'painAmount',
    dataPoints: painAmountDataPoints,
    type: 'stackedBarChart',
    legend: locales['other.episodesQuantity'],
    stackedBarChartProps: { interval: 'daily' },
  });
  painData.push({
    id: 'painDuration',
    dataPoints: painDurationDataPoints,
    type: 'lineGraph',
    useAdditionalScale: true,
    legend: locales['other.episodesDuration'],
    linegraphProps: { xAxisLineBreaks: xLineBreaks },
  });
  allSymptomsData.unshift({ data: painData, id: locales['pain.title'] });
  return allSymptomsData;
};

export const convertDesToGraph = (
  docs: IDocumentWithTypeV2<'des'>[],
  dateFromPartialUpdateTimeframe: (date: PartialDate, time?: Time, marker?: true) => Date,
  uiLang: Locale,
): Array<IData> | undefined => {
  if (docs.length === 0) return;
  const dataPoints: IDataPoint[] = [];
  const locales = des.localizations[uiLang];

  docs.forEach(d => {
    if (!isPartialDate(d.date)) return;
    if (d.dyspneaIntensity) {
      let numberValue;
      switch (d.dyspneaIntensity) {
        case 'canWalkAtOwnPaceRegardlessOfDistance':
          numberValue = 1;
          break;
        case 'dyspneaWhenWalkingAtOwnPaceOver100Meters':
          numberValue = 2;
          break;
        case 'dyspneaWhenWalkingAtOwnPaceAtHomeOrInTheWard':
          numberValue = 3;
          break;
        case 'dyspneaWhenMovingInBedOrGettingUp':
          numberValue = 4;
          break;
        case 'dyspneaWhenTalking':
          numberValue = 5;
          break;
        case 'dyspneaAtRest':
          numberValue = 6;
          break;
        default:
          break;
      }
      numberValue &&
        dataPoints.push({
          id: 'des',
          date: dateFromPartialUpdateTimeframe(d.date),
          value: numberValue,
          description: locales[`opts.${d.dyspneaIntensity}`],
        });
    }
  });
  return [{ dataPoints, id: 'des', type: 'lineGraph', legend: 'DES' }];
};

export const convertEssToGraph = (
  docs: IDocumentWithTypeV2<'ess'>[],
  dateFromPartialUpdateTimeframe: (date: PartialDate, time?: Time, marker?: true) => Date,
): Array<IData> | undefined => {
  if (docs.length === 0) return;
  const dataPoints: IDataPoint[] = [];

  docs.forEach(d => {
    if (!isPartialDate(d.date)) return;
    const score = calculateESSScore(d);
    if (isNumber(score)) {
      dataPoints.push({ id: 'ess', date: dateFromPartialUpdateTimeframe(d.date), value: score });
    }
  });
  return [{ dataPoints, id: 'ess', type: 'lineGraph', legend: 'ESS' }];
};

export const convertTdcsMyTreatmentsToGraph = (
  docs: IDocumentWithTypeV2<'tdcsReport'>[],
  dateFromPartialUpdateTimeFrame: (date: PartialDate, time?: Time, marker?: true) => Date,
  fm: (id: string) => string,
  treatmentPeriodDocs: IDocumentWithTypeV2<'ninmtTreatmentPeriod'>[],
): Array<IData> | undefined => {
  if (docs.length === 0) return;
  const treatmentPeriodsDateValues = getNinmtTreatmentPeriodsDateValues(treatmentPeriodDocs);
  const allTreatmentNamesDataPoints: Array<IDataPoint[]> = [];

  docs.forEach(d => {
    if (!d.date) return;
    const docDateValue = partialDateToValue(d.date);
    let treatmentPeriodIndex = treatmentPeriodsDateValues.findIndex(
      (dv, i) => dv <= docDateValue && (treatmentPeriodsDateValues[i + 1] ?? Number.MAX_SAFE_INTEGER) > docDateValue,
    );
    if (treatmentPeriodIndex < 0) treatmentPeriodIndex = 0;

    d.treatments?.forEach(t => {
      const treatmentName = t.name === 'other' && t.nameOther ? t.nameOther : t.name;
      const tNameDataPointIndex = allTreatmentNamesDataPoints.findIndex(
        tndp => tndp[0]?.id === `${treatmentName}-${treatmentPeriodIndex}`,
      );

      if (tNameDataPointIndex > -1) {
        d.date &&
          allTreatmentNamesDataPoints[tNameDataPointIndex]?.push({
            date: dateFromPartialUpdateTimeFrame(d.date),
            value: t.amountOfTreatmentSessions ?? '',
            id: `${treatmentName}-${t.specifier ?? ''}-${treatmentPeriodIndex}`,
            additionalValue: t.name === 'other' && t.nameOther ? t.nameOther : undefined,
            title: `${t.amountOfTreatmentSessions} ${fm('graph.treatmentsPerWeek')}`,
          });
      } else {
        d.date &&
          allTreatmentNamesDataPoints.push([
            {
              date: dateFromPartialUpdateTimeFrame(d.date),
              value: t.amountOfTreatmentSessions ?? '',
              id: `${treatmentName}-${t.specifier ?? ''}-${treatmentPeriodIndex}`,
              additionalValue: t.name === 'other' && t.nameOther ? t.nameOther : undefined,
              title: `${t.amountOfTreatmentSessions} ${fm('graph.treatmentsPerWeek')}`,
            },
          ]);
      }
    });
  });

  return allTreatmentNamesDataPoints.reverse().map(dp => {
    const idSplitted = dp[0]?.id.split('-');
    const legend = dp[0]?.additionalValue
      ? `${idSplitted[0]}${idSplitted[1].length > 0 ? ` ${idSplitted[1]}` : ''}`
      : `${fm(`tdcs.subjectOfTreatmentNames.${idSplitted[0]}`)}${idSplitted[1]?.length > 0 ? ` ${idSplitted[1]}` : ''}`;
    return { dataPoints: dp, id: `${dp[0].id}`, legend: legend, type: 'lineGraph' };
  });
};

export const convertPatientReportedMedicationsToTimeline = (
  docs: Array<IDocumentWithTypeV2<'ninmtPreInquiry' | 'treatmentMonitoringInquiry'>>,
  dateFromPartialUpdateTimeFrame: (date: PartialDate, time?: Time, marker?: true) => Date,
  fm: (id: string) => string,
): Array<IAddon> | undefined => {
  if (docs.length === 0) return;
  const addons: Array<IAddon> = [];
  const docsSortedOldest1st = docs
    .slice()
    .sort((a, b) => a._docCreateDate - b._docCreateDate)
    .sort((a, b) => sortPartialDate(a.date, b.date));
  docsSortedOldest1st.forEach((doc, i, arr) => {
    if (!isPartialDate(doc.date)) return;
    const events: IEvent[] = [];
    const prevDocMedicationDefaults =
      i === 0
        ? undefined
        : docsSortedOldest1st[i - 1]?.medication?.map(m => ({
            medicationName: m.medicationName,
            medicationDosage: m.medicationDosage,
          }));
    const medicationDefaults = doc.medication?.map(m => ({
      medicationName: m.medicationName,
      medicationDosage: m.medicationDosage,
    }));

    const modifiedMeds =
      medicationDefaults
        ?.map(md => {
          const prevDosing = prevDocMedicationDefaults?.find(
            pmd => md.medicationName === pmd.medicationName,
          )?.medicationDosage;
          if (prevDosing && prevDosing !== md.medicationDosage) return md;
          return undefined;
        })
        .filter(mm => mm) ?? [];
    const addedMeds = difference(medicationDefaults ?? [], prevDocMedicationDefaults ?? []).filter(
      m => !modifiedMeds.some(m2 => equals(m, m2)),
    );
    const removedMeds = difference(prevDocMedicationDefaults ?? [], medicationDefaults ?? []).filter(
      m => !modifiedMeds.some(m2 => m.medicationName === m2?.medicationName),
    );

    const hasModifications =
      (doc._type === 'treatmentMonitoringInquiry' && doc.medication?.some(m => m.onDemandDosingWithinWeek)) ??
      (prevDocMedicationDefaults && medicationDefaults && !equals(prevDocMedicationDefaults, medicationDefaults)) ??
      (addedMeds.length > 0 || removedMeds.length > 0 || modifiedMeds.length > 0);

    // NINMT pre-inquiry medication events are currently not included in graph, but differences with treatment monitoring are considered.
    doc._type === 'treatmentMonitoringInquiry' && hasModifications
      ? events.push({
          date: dateFromPartialUpdateTimeFrame(doc.date),
          eventType: 'modification',
          title: fm('graph.patientReportedMedication'),
          description: [
            {
              title: fm('myService.ninmt.treatmentMonitoringInquiry.addedMedications'),
              values: (
                <>
                  {addedMeds.map((am, j) => (
                    <div
                      key={`${am.medicationName}-${j}`}
                      style={{
                        backgroundColor: j % 2 !== 0 ? colors.lightestGray : undefined,
                        display: 'flex',
                        justifyContent: 'space-between',
                      }}
                    >
                      <span>{am.medicationName}</span>
                      <span>{am.medicationDosage}</span>
                    </div>
                  ))}
                </>
              ),
              condition: addedMeds.length > 0,
            },
            {
              title: fm('myService.ninmt.treatmentMonitoringInquiry.removedMedications'),
              values: (
                <>
                  {removedMeds.map((rm, j) => (
                    <div
                      key={`${rm.medicationName}-${j}`}
                      style={{
                        backgroundColor: j % 2 !== 0 ? colors.lightestGray : undefined,
                        display: 'flex',
                        justifyContent: 'space-between',
                      }}
                    >
                      <span>{rm.medicationName}</span>
                      <span>{rm.medicationDosage}</span>
                    </div>
                  ))}
                </>
              ),
              condition: removedMeds.length > 0,
            },
            {
              title: fm('myService.ninmt.treatmentMonitoringInquiry.modifiedMedications'),
              values: (
                <>
                  {modifiedMeds.map((mm, j) => (
                    <div
                      key={`${mm?.medicationName}-${j}`}
                      style={{
                        backgroundColor: j % 2 !== 0 ? colors.lightestGray : undefined,
                        display: 'flex',
                        justifyContent: 'space-between',
                      }}
                    >
                      <span>{mm?.medicationName}</span>
                      <span>{mm?.medicationDosage}</span>
                    </div>
                  ))}
                </>
              ),
              condition: modifiedMeds.length > 0,
            },
            doc.medication?.some(m => m.onDemandDosingWithinWeek)
              ? {
                  title: (
                    <span style={{ paddingRight: '2.5rem' }}>
                      {fm('myService.ninmt.treatmentMonitoringInquiry.onDemandDosages')}
                    </span>
                  ),
                  values: (
                    <>
                      {doc.medication
                        .filter(m => m.onDemandDosingWithinWeek)
                        .map((em, j) => (
                          <div
                            key={j}
                            style={{
                              backgroundColor: j % 2 !== 0 ? colors.lightestGray : undefined,
                              display: 'flex',
                              justifyContent: 'space-between',
                              paddingRight: '1rem',
                            }}
                          >
                            <span>{em.medicationName}</span>
                            <span>{em.onDemandDosingWithinWeek}</span>
                          </div>
                        ))}
                    </>
                  ),
                }
              : { title: fm('graph.noMedicationsTakenDuringTheWeekOnDemand') },
          ],
        })
      : i > 0 && arr.slice(0, i).some(d => Array.isArray(d.medication) && d.medication.length > 0)
        ? events.push({
            date: dateFromPartialUpdateTimeFrame(doc.date),
            title: fm('graph.noChangesInMedications'),
            eventType: 'selfReport',
            description: [{ title: fm('graph.noMedicationsTakenDuringTheWeekOnDemand') }],
          })
        : void 0;
    events.length > 0 &&
      addons.push({
        id: 'patientReportedMedications',
        title: '',
        titleDescription: undefined,
        events,
        items: [],
      });

    return;
  });
  return addons.length > 0 ? addons : undefined;
};

export const convertPedsqlFatigueToGraph = (
  docs: IDocumentWithTypeV2<'pedsql'>[],
  dateFromPartialUpdateTimeFrame: (date: PartialDate, time?: Time, marker?: true) => Date,
  fm: (id: string) => string,
  uiLang: Locale,
): Array<IData> | undefined => {
  if (docs.length === 0) return;
  const pedsqlFatigueDocs = docs.filter(d => d.module === 'fatigue');
  if (pedsqlFatigueDocs.length === 0) return;
  const locales = pedsql.localizations[uiLang];

  const dataPoints: IDataPoint[] = [];

  pedsqlFatigueDocs.forEach(d => {
    if (!isPartialDate(d.date)) return;
    const progress = pedsql.calculateProgress(d);
    if (progress.yielded === null) return;
    const subValues = pedsql.utils.getSubFieldValues(d);
    const versionText = pedsql.utils.versionText(d.type, d.version, d.module);
    dataPoints.push({
      id: 'pedsqlFatigue',
      date: dateFromPartialUpdateTimeFrame(d.date),
      value: progress.yielded,
      title: `${progress.yielded} / ${progress.ceiling}`,
      pointType: d.type === 'parentProxyReport' ? 'rect' : 'circle',
      description: [
        { title: fm('general.version'), values: versionText ? locales[versionText] : '' },
        {
          title: fm('myService.reporter'),
          values: d.type === 'selfReport' ? fm('myService.patient') : fm('myService.delegate'),
        },
        { title: fm('graph.selfReport.pedsql.generalFatigue'), values: `${subValues?.generalFatigue} / 24` },
        { title: fm('graph.selfReport.pedsql.sleepRestFatigue'), values: `${subValues?.sleepRestFatigue} / 24` },
        { title: fm('graph.selfReport.pedsql.mentalFatigue'), values: `${subValues?.mentalFatigue} / 24` },
      ],
    });
  });
  return [{ id: 'pedsqlFatigue', type: 'lineGraph', legend: fm('myService.sma.opts.pedsqlFatigue'), dataPoints }];
};

export const convertPedsqlNeuromuscularToGraph = (
  docs: IDocumentWithTypeV2<'pedsql'>[],
  dateFromPartialUpdateTimeFrame: (date: PartialDate, time?: Time, marker?: true) => Date,
  fm: (id: string) => string,
  uiLang: Locale,
): Array<IData> | undefined => {
  if (docs.length === 0) return;
  const pedsqlNeuromuscularDocs = docs.filter(d => d.module === 'neuromuscular');
  if (pedsqlNeuromuscularDocs.length === 0) return;
  const locales = pedsql.localizations[uiLang];

  const dataPointsYC: IDataPoint[] = [];
  const dataPointsRest: IDataPoint[] = [];

  pedsqlNeuromuscularDocs.forEach(d => {
    if (!isPartialDate(d.date)) return;
    const progress = pedsql.calculateProgress(d);
    if (progress.yielded === null) return;
    const subValues = pedsql.utils.getSubFieldValues(d);
    const versionText = pedsql.utils.versionText(d.type, d.version, d.module);
    if (d.type === 'selfReport' && d.version === 'youngChild') {
      dataPointsYC.push({
        id: 'pedsqlNeuromuscularYC',
        date: dateFromPartialUpdateTimeFrame(d.date),
        value: progress.yielded,
        title: `${progress.yielded} / ${progress.ceiling}`,
        additionalValue: pedsql.utils.versionText(d.type, d.version, d.module) ?? '', // Saving the legend text here
        description: [
          { title: fm('myService.reporter'), values: fm('myService.patient') },
          {
            title: fm('graph.selfReport.pedsql.neuromuscularIllness'),
            values: `${subValues?.neuromuscularIllness} / 68`,
          },
        ],
      });
    } else {
      dataPointsRest.push({
        id: 'pedsqlNeuromuscularRest',
        date: dateFromPartialUpdateTimeFrame(d.date),
        value: progress.yielded,
        title: `${progress.yielded} / ${progress.ceiling}`,
        pointType: d.type === 'parentProxyReport' ? 'rect' : 'circle',
        description: [
          { title: fm('general.version'), values: versionText ? locales[versionText] : '' },
          {
            title: fm('myService.reporter'),
            values: d.type === 'selfReport' ? fm('myService.patient') : fm('myService.delegate'),
          },
          {
            title: fm('graph.selfReport.pedsql.neuromuscularIllness'),
            values: `${subValues?.neuromuscularIllness} / 68`,
          },
          { title: fm('graph.selfReport.pedsql.communication'), values: `${subValues?.communication} / 12` },
          {
            title: fm('graph.selfReport.pedsql.familyCircumstances'),
            values: `${subValues?.familyCircumstances} / 20`,
          },
        ],
      });
    }
  });
  return [
    {
      id: 'pedsqlNeuromuscularRest',
      type: 'lineGraph',
      legend: fm('myService.sma.opts.pedsqlNeuromuscular'),
      dataPoints: dataPointsRest,
    },
    {
      id: 'pedsqlNeuromuscularYC',
      type: 'lineGraph',
      legend: locales[dataPointsYC[0]?.additionalValue ?? ''] ?? '',
      dataPoints: dataPointsYC,
    },
  ];
};

export const convertPdq8ToGraph = (
  docs: IDocumentWithTypeV2<'pdq8'>[],
  dateFromPartialUpdateTimeframe: (date: PartialDate, time?: Time, marker?: true) => Date,
): Array<IData> | undefined => {
  if (docs.length === 0) return;
  const dataPoints: IDataPoint[] = [];

  docs.forEach(d => {
    if (!isPartialDate(d.date)) return;
    const score = calculatePDQ8Score(d);
    if (!isNumber(score)) return;
    const value = Math.round((score / 32) * 100);
    dataPoints.push({
      id: 'pdq8',
      date: dateFromPartialUpdateTimeframe(d.date),
      value,
      title: `${value} %`,
    });
  });
  return [{ dataPoints, id: 'pdq8', type: 'lineGraph', legend: 'PDQ-8' }];
};

export const convertMgQol15rToGraph = (
  docs: IDocumentWithTypeV2<'mgQol15r'>[],
  dateFromPartialUpdateTimeframe: (date: PartialDate, time?: Time, marker?: true) => Date,
  fm: (id: string) => string,
): Array<IData> | undefined => {
  if (docs.length === 0) return;
  const dataPoints: IDataPoint[] = [];

  docs.forEach(d => {
    if (!isPartialDate(d.date)) return;
    const totalScore = mgQol15r.calculators.calculateMGQoL15rTotalScore(d);
    if (isNumber(totalScore)) {
      dataPoints.push({
        id: 'mgQol15r',
        date: dateFromPartialUpdateTimeframe(d.date),
        value: totalScore,
      });
    }
  });
  return [{ dataPoints, id: 'mgQol15r', type: 'lineGraph', legend: fm('graph.selfReport.mgQol15r.title') }];
};
